import {
    Directive,
    Input,
    ElementRef,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import { CurrencyService } from 'src/app/core/services/currency.service';
import {
    formatCurrencyForDisplay,
    CurrencyFormatOptions
} from 'src/app/core/utils/currency.utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Directiva para aplicar formateo de moneda directamente a un elemento DOM.
 *
 * Uso:
 * <span [appCurrencyFormat]="1234.56"></span>                          // Usa moneda actual
 * <span [appCurrencyFormat]="price" currencyCode="USD"></span>         // Moneda específica
 * <span [appCurrencyFormat]="price" [locale]="'es-ES'"></span>         // Con locale
 * <span [appCurrencyFormat]="price" minimumFractionDigits="0"></span>  // Sin decimales
 *
 * Propiedades:
 * - appCurrencyFormat: número a formatear
 * - currencyCode: código ISO de moneda (opcional, usa valor del usuario)
 * - locale: locale BCP47 (opcional)
 * - minimumFractionDigits: dígitos mínimos después del decimal (default: 2)
 * - maximumFractionDigits: dígitos máximos después del decimal (default: 2)
 */
@Directive({
    selector: '[appCurrencyFormat]'
})
export class CurrencyFormatDirective implements OnInit, OnDestroy, OnChanges {
    @Input() appCurrencyFormat: number | null | undefined;
    @Input() currencyCode?: string | null;
    @Input() locale?: string | null;
    @Input() minimumFractionDigits = 2;
    @Input() maximumFractionDigits = 2;

    private destroy$ = new Subject<void>();
    private currentCurrencyCode = 'EUR';

    constructor(
        private el: ElementRef<HTMLElement>,
        private currencyService: CurrencyService
    ) { }

    ngOnInit(): void {
        this.currencyService.currencyCode$
            .pipe(takeUntil(this.destroy$))
            .subscribe(code => {
                this.currentCurrencyCode = code;
                this.updateDisplay();
            });

        this.currencyService.forceZeroDecimals$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateDisplay();
            });

        this.updateDisplay();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['appCurrencyFormat'] ||
            changes['currencyCode'] ||
            changes['locale'] ||
            changes['minimumFractionDigits'] ||
            changes['maximumFractionDigits']
        ) {
            this.updateDisplay();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private updateDisplay(): void {
        const amount = this.appCurrencyFormat;

        if (amount === null || amount === undefined || !Number.isFinite(amount)) {
            this.el.nativeElement.textContent = '—';
            return;
        }

        const options: CurrencyFormatOptions = {
            amount,
            currencyCode: this.currencyCode ?? this.currentCurrencyCode,
            locale: this.locale,
            minimumFractionDigits: this.minimumFractionDigits,
            maximumFractionDigits: this.maximumFractionDigits,
            showSymbol: true,
            symbolPosition: 'auto'
        };

        const result = formatCurrencyForDisplay(options);
        this.el.nativeElement.textContent = result.formatted;
    }
}
